// Код ошибки и номер операции (FR-028; 002, contracts/errors.md, «Номер операции»):
// «Код SYS-001 · операция 7F3K-92QD-1A2B» моноширинным и кнопка-иконка «Скопировать номер».
// Номер — группами по четыре, диктор читает его группами через пробел. Скопировано — тост
// «Номер скопирован»; браузер не дал записать в буфер — номер выделяется, тост с CLIP-001.
import { formatOperation, normalizeOperation, operationCopyFailure } from '@kp/contracts';
import { Copy } from 'lucide-react';
import { useRef } from 'react';
import { cx } from '../lib/cx.ts';
import { useOptionalToast } from '../lib/toast-context.ts';
import { IconButton } from './IconButton.tsx';

/** Код интерфейса: браузер не дал записать в буфер обмена (contracts/errors.md). */
export const OPERATION_COPY_ERROR_CODE = 'CLIP-001';

/** Что попадает в буфер обмена: «Код SYS-001, операция 7F3K-92QD-1A2B». */
export function operationCopyText(code: string, operation: string): string {
  return `Код ${code}, операция ${formatOperation(normalizeOperation(operation))}`;
}

/** Что читает диктор: «Код SYS-001, операция 7F3K 92QD 1A2B» — номер группами. */
export function operationSpokenText(code: string, operation: string): string {
  return `Код ${code}, операция ${formatOperation(normalizeOperation(operation)).replaceAll('-', ' ')}`;
}

export interface OperationCodeProps {
  /** Код ошибки: «SYS-001». */
  code: string;
  /** Номер операции из ответа сервера: 12 знаков, дефисы не нужны. */
  operation: string;
  /** Сторона подсказки кнопки; в тосте — сверху. */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

export function OperationCode({ code, operation, tooltipSide, className }: OperationCodeProps) {
  const toast = useOptionalToast();
  const numberRef = useRef<HTMLSpanElement>(null);
  const shown = formatOperation(normalizeOperation(operation));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(operationCopyText(code, operation));
      toast?.ok('Номер скопирован', { duration: 'short' });
    } catch {
      // Буфер обмена недоступен — номер выделяется, его можно скопировать вручную.
      const node = numberRef.current;
      const selection = window.getSelection();
      if (node && selection) selection.selectAllChildren(node);
      toast?.error(`${operationCopyFailure.message}. ${operationCopyFailure.nextStep}`, {
        code: OPERATION_COPY_ERROR_CODE,
        duration: 'long',
      });
    }
  };

  return (
    <span className={cx('kp-opcode', className)}>
      <span className="kp-code">
        Код {code} · операция{' '}
        {/* На экране — с дефисами; диктор читает скрытую строку: группами через пробел. */}
        <span ref={numberRef} className="kp-opcode-num" aria-hidden="true">
          {shown}
        </span>
        <span className="kp-sr-only">{shown.replaceAll('-', ' ')}</span>
      </span>
      <IconButton
        icon={Copy}
        label="Скопировать номер"
        size="sm"
        tooltipSide={tooltipSide}
        className="kp-opcode-copy"
        onClick={() => void copy()}
      />
    </span>
  );
}
